import { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  LayoutDashboard,
  Package,
  Truck,
  Wrench,
  ShoppingCart,
  MapPin,
  AlertTriangle,
  BarChart3,
  Settings,
  ChevronLeft,
  ChevronRight,
  Moon,
  Sun,
  LogOut,
  User,
  Bell,
  Search,
  Menu,
} from 'lucide-react';

interface NavItem {
  title: string;
  icon: React.ReactNode;
  href?: string;
  children?: { title: string; href: string }[];
}

const navigation: NavItem[] = [
  {
    title: 'Dashboard',
    icon: <LayoutDashboard className="h-5 w-5" />,
    href: '/dashboard',
  },
  {
    title: 'Inventory',
    icon: <Package className="h-5 w-5" />,
    children: [
      { title: 'Inventory', href: '/inventory' },
      { title: 'Consumption', href: '/consumption' },
      { title: 'Quick Scan', href: '/quick-scan' },
    ],
  },
  {
    title: 'Equipment',
    icon: <Wrench className="h-5 w-5" />,
    children: [
      { title: 'Equipment', href: '/equipment' },
      { title: 'Work Orders', href: '/work-orders' },
      { title: 'Maintenance', href: '/maintenance' },
    ],
  },
  {
    title: 'Procurement',
    icon: <ShoppingCart className="h-5 w-5" />,
    children: [
      { title: 'Purchase Orders', href: '/purchase-orders' },
      { title: 'Goods Receipt', href: '/goods-receipt' },
      { title: 'PO Visibility', href: '/po-visibility' },
      { title: 'Suppliers', href: '/suppliers' },
    ],
  },
  {
    title: 'Transportation',
    icon: <Truck className="h-5 w-5" />,
    children: [
      { title: 'TMS Dashboard', href: '/tms' },
      { title: 'Shipment Planner', href: '/shipment-planner' },
      { title: 'Shipments', href: '/shipments' },
      { title: 'Track & Trace', href: '/track-trace' },
      { title: 'Carriers', href: '/tms/carriers' },
      { title: 'Freight RFQ', href: '/tms/rfq' },
      { title: 'RFQ Approvals', href: '/tms/rfq-approvals' },
      { title: 'RFQ Analytics', href: '/tms/rfq-analytics' },
      { title: 'Rates', href: '/tms/rates' },
      { title: 'Rate Dashboard', href: '/tms/rate-dashboard' },
      { title: 'Rate Import', href: '/tms/rate-import' },
      { title: 'Rate Analytics', href: '/tms/rate-analytics' },
      { title: 'Rate Templates', href: '/tms/rate-templates' },
      { title: 'Charge Types', href: '/tms/charge-types' },
      { title: 'Vendor Groups', href: '/tms/vendor-groups' },
      { title: 'Terms & Conditions', href: '/tms/terms' },
    ],
  },
  {
    title: 'Yard & Storage',
    icon: <MapPin className="h-5 w-5" />,
    children: [
      { title: 'Yard Dashboard', href: '/yard' },
      { title: 'Storage Locations', href: '/yard/locations' },
      { title: 'Putaway', href: '/yard/putaway' },
      { title: 'Job Kits', href: '/yard/job-kits' },
      { title: 'Picking', href: '/yard/picking' },
      { title: 'Pipe Tally', href: '/yard/pipe-tally' },
      { title: 'Asset Search', href: '/yard/asset-search' },
    ],
  },
  {
    title: 'Risk & Compliance',
    icon: <AlertTriangle className="h-5 w-5" />,
    children: [
      { title: 'Risk Management', href: '/risk' },
      { title: 'Compliance', href: '/compliance' },
    ],
  },
  {
    title: 'Analytics',
    icon: <BarChart3 className="h-5 w-5" />,
    children: [
      { title: 'Analytics', href: '/analytics' },
      { title: 'Forecasting', href: '/forecasting' },
      { title: 'Simulation', href: '/simulation' },
    ],
  },
  {
    title: 'Admin',
    icon: <Settings className="h-5 w-5" />,
    children: [
      { title: 'Companies', href: '/admin/companies' },
      { title: 'Roles & Permissions', href: '/admin/roles' },
      { title: 'Users', href: '/admin/users' },
      { title: 'Partner Networks', href: '/admin/partners' },
      { title: 'Audit Logs', href: '/admin/audit-logs' },
      { title: 'Approvals', href: '/approvals' },
      { title: 'Alerts', href: '/alerts' },
      { title: 'Locations', href: '/locations' },
    ],
  },
];

export function MainLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState<string[]>(['Dashboard']);
  const { user, logout } = useAuth();
  const { setTheme, resolvedTheme } = useTheme();
  const location = useLocation();

  const toggleExpanded = (title: string) => {
    setExpandedItems((prev) =>
      prev.includes(title) ? prev.filter((t) => t !== title) : [...prev, title]
    );
  };

  const isActive = (href: string) => location.pathname === href;
  const isParentActive = (children?: { href: string }[]) =>
    children?.some((child) => location.pathname === child.href);

  return (
    <div className="flex h-screen bg-background">
      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex flex-col border-r bg-sidebar transition-all duration-200',
          collapsed ? 'w-16' : 'w-56',
          'lg:relative'
        )}
      >
        {/* Logo */}
        <div className="flex h-16 items-center justify-between border-b px-4">
          {!collapsed && (
            <Link to="/" className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg gradient-primary">
                <span className="text-sm font-bold text-white">W</span>
              </div>
              <span className="font-semibold text-sidebar-foreground">Weatherford IQ</span>
            </Link>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="hidden lg:flex"
            onClick={() => setCollapsed(!collapsed)}
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-2">
          {navigation.map((item) => (
            <div key={item.title} className="mb-1">
              {item.href ? (
                <Link
                  to={item.href}
                  className={cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    isActive(item.href)
                      ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                      : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                  )}
                >
                  {item.icon}
                  {!collapsed && <span>{item.title}</span>}
                </Link>
              ) : (
                <>
                  <button
                    onClick={() => toggleExpanded(item.title)}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                      isParentActive(item.children)
                        ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                        : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                    )}
                  >
                    {item.icon}
                    {!collapsed && (
                      <>
                        <span className="flex-1 text-left">{item.title}</span>
                        <ChevronRight
                          className={cn(
                            'h-4 w-4 transition-transform',
                            expandedItems.includes(item.title) && 'rotate-90'
                          )}
                        />
                      </>
                    )}
                  </button>
                  {!collapsed && expandedItems.includes(item.title) && item.children && (
                    <div className="ml-4 mt-1 space-y-1 border-l pl-4">
                      {item.children.map((child) => (
                        <Link
                          key={child.href}
                          to={child.href}
                          className={cn(
                            'block rounded-lg px-3 py-1.5 text-sm transition-colors',
                            isActive(child.href)
                              ? 'bg-sidebar-primary text-sidebar-primary-foreground'
                              : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                          )}
                        >
                          {child.title}
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </nav>
      </aside>

      {/* Main content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header */}
        <header className="flex h-16 items-center justify-between border-b bg-background px-4 lg:px-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <Menu className="h-5 w-5" />
            </Button>
            <div className="relative hidden md:block">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search... (⌘K)"
                className="h-9 w-64 rounded-lg border bg-muted/50 pl-9 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Theme toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
            >
              <Sun className="h-5 w-5 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
              <Moon className="absolute h-5 w-5 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
            </Button>

            {/* Notifications */}
            <Button variant="ghost" size="icon" className="relative">
              <Bell className="h-5 w-5" />
              <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-destructive" />
            </Button>

            {/* User menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground">
                    <User className="h-4 w-4" />
                  </div>
                  <span className="hidden md:inline-block">{user?.fullName || 'User'}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.fullName}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem>
                  <User className="mr-2 h-4 w-4" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <Settings className="mr-2 h-4 w-4" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
